import React from 'react';
import { Formik } from 'formik';
import { isNil } from 'lodash';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import NonViewCommunicationCardHeaderContactDetails from '~/components/communications/CommunicationCard/NonViewCommunicationCardHeaderContactDetails';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import Text from '~/components/core/TextComponents/Text';
import type { ContactFullModel } from '~/server_shared/swagger/data-contracts';

import { TEXTS } from './texts';

interface DocumentRequestRecipientDialogProps {
  title: string;
  intro: string;
  primaryLabel: string;
  onSubmit: (contact: ContactFullModel) => Promise<void> | void;
  onClose: () => void;
  // Why the picked contact cannot receive what this dialog leads to, if it cannot; the reason blocks the submit.
  getContactBlockedReason?: (contact: ContactFullModel) => string | undefined;
}

interface RecipientFormValues {
  contact?: ContactFullModel;
  contact_id: number | '';
  contact_full_name: string;
}

const buildValidationSchema = (getContactBlockedReason?: (contact: ContactFullModel) => string | undefined) =>
  Yup.object().shape({
    contact_id: Yup.number().required(TEXTS.CONTACT_REQUIRED).typeError(TEXTS.CONTACT_REQUIRED),
    contact: Yup.mixed().test('can-receive', '', function canReceive(contact?: ContactFullModel) {
      const reason = isNil(contact) || isNil(getContactBlockedReason) ? undefined : getContactBlockedReason(contact);
      return isNil(reason) ? true : this.createError({ message: reason });
    }),
  });

// A document request link is claim-level and remembers no recipient, so whoever it goes to is picked here, with
// the contact picker the new video call card uses. Nothing is preselected.
const DocumentRequestRecipientDialog: React.FC<DocumentRequestRecipientDialogProps> = ({
  title,
  intro,
  primaryLabel,
  onSubmit,
  onClose,
  getContactBlockedReason,
}) => (
  <Formik<RecipientFormValues>
    initialValues={{ contact: undefined, contact_id: '', contact_full_name: '' }}
    validationSchema={buildValidationSchema(getContactBlockedReason)}
    onSubmit={async (values) => {
      if (!isNil(values.contact)) await onSubmit(values.contact);
    }}
  >
    {({ handleSubmit, isSubmitting }) => (
      <CardDialog
        isDialog
        title={title}
        onClose={onClose}
        preventClose={isSubmitting}
        maxWidth="sm"
        fullWidth
        footerActions={
          <DialogFooterActions
            secondaryLabel={TEXTS.CANCEL_BUTTON}
            onClickSecondary={onClose}
            primaryLabel={primaryLabel}
            onClickPrimary={() => handleSubmit()}
            disabled={isSubmitting}
          />
        }
      >
        <div className="flex flex-col gap-16">
          <Text variant={Text.VARIANTS.SM}>{intro}</Text>
          <NonViewCommunicationCardHeaderContactDetails
            enableChoosingContact
            dynamicContactLabel={TEXTS.CONTACT_LABEL}
          />
        </div>
      </CardDialog>
    )}
  </Formik>
);

export default DocumentRequestRecipientDialog;
